import { LogRecord } from "@fairfleet/geotab";
import Box from "@mui/material/Box";
import { LatLngBoundsExpression, LatLngExpression } from "leaflet";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { CircleMarker, MapContainer, Polyline, TileLayer, Tooltip as LeafletTooltip, useMap } from "react-leaflet";
import { useAuditTimeZone } from "../../hooks/useAuditTimeZone";
import { AuditTrip, displayReason } from "../../models/AuditTrip";
import { AUDIT_TOKENS } from "../../theme/auditTokens";
import { formatClockInZone } from "../../utils/timeZone";
import { MapLegend } from "./MapLegend";
import { MAP_TILE_LAYERS, MapTileId } from "@/constants/MapTiles";
import "leaflet/dist/leaflet.css";

export interface TripLine {
  trip: AuditTrip;
  positions: LatLngExpression[];
  /**
   * False when every position of the trip is the same point: the device had no GPS fix and kept
   * its last position while the vehicle's odometer (the source of the distance) moved on.
   */
  hasTrack: boolean;
}

interface Props {
  /** Only the lines to draw; the map fits them. */
  lines: TripLine[];
  tileId: MapTileId;
}

/** Splits a device's log records into one polyline per trip window. */
export function buildTripLines(trips: AuditTrip[], records: LogRecord[]): TripLine[] {
  const sorted = [...records].sort((a, b) => a.dateTime.getTime() - b.dateTime.getTime());
  return trips.map(trip => {
    const points = sorted
      .filter(r => r.dateTime >= trip.start && r.dateTime <= (trip.stop ?? trip.start))
      .filter(r => Number.isFinite(r.latitude) && Number.isFinite(r.longitude));
    const distinct = new Set(points.map(r => `${r.latitude},${r.longitude}`));
    return {
      trip,
      positions: points.map(r => [r.latitude, r.longitude] as LatLngExpression),
      hasTrack: distinct.size > 1,
    };
  });
}

export function FitBounds({ bounds, maxZoom }: { bounds: LatLngBoundsExpression | null; maxZoom?: number }) {
  const map = useMap();
  useEffect(() => {
    if (bounds) map.fitBounds(bounds, { padding: [32, 32], maxZoom });
  }, [map, bounds, maxZoom]);
  return null;
}

export function TripMap({ lines, tileId }: Props) {
  const { t } = useTranslation();
  const timeZone = useAuditTimeZone();
  const layer = MAP_TILE_LAYERS.find(l => l.id === tileId) ?? MAP_TILE_LAYERS[0];

  const bounds = useMemo<LatLngBoundsExpression | null>(() => {
    const points = lines.flatMap(l => l.positions) as [number, number][];
    return points.length > 0 ? points : null;
  }, [lines]);
  const withoutTrack = lines.filter(line => line.positions.length > 0 && !line.hasTrack).length;

  return (
    <Box sx={{ position: "relative", flex: 1, minHeight: 320, "& .leaflet-container": { height: "100%", width: "100%", fontFamily: AUDIT_TOKENS.fontFamily } }}>
      <MapContainer center={[51.16, 10.45]} zoom={6} style={{ height: "100%", width: "100%" }} aria-label={t("logbookAudit.map.title")}>
        <TileLayer key={layer.id} url={layer.url} attribution={layer.attribution} maxZoom={layer.maxZoom} />
        {lines.map(({ trip, positions, hasTrack }) => {
          if (positions.length === 0) return null;
          const color = AUDIT_TOKENS.map[trip.taxClass];
          const label = `${formatClockInZone(trip.start, timeZone)} – ${formatClockInZone(trip.stop ?? trip.start, timeZone)} · ${displayReason(trip) ?? t("logbookAudit.grid.missingReason")} · ${t(`logbookAudit.taxClass.${trip.taxClass}`)}`;
          if (!hasTrack) {
            return (
              <CircleMarker key={trip.id} center={positions[0]} radius={7} pathOptions={{ color, fillColor: "#fff", fillOpacity: 1, weight: 2, dashArray: "3 3" }}>
                <LeafletTooltip>{`${label} · ${t("logbookAudit.map.noTrackTrip")}`}</LeafletTooltip>
              </CircleMarker>
            );
          }
          return (
            <Box component="span" key={trip.id} sx={{ display: "contents" }}>
              <Polyline positions={positions} pathOptions={{ color, weight: 4, opacity: 0.85 }}>
                <LeafletTooltip sticky>{label}</LeafletTooltip>
              </Polyline>
              <CircleMarker center={positions[0]} radius={6} pathOptions={{ color, fillColor: color, fillOpacity: 1, weight: 2 }}>
                <LeafletTooltip>{t("logbookAudit.map.start")}</LeafletTooltip>
              </CircleMarker>
              <CircleMarker center={positions[positions.length - 1]} radius={6} pathOptions={{ color, fillColor: "#fff", fillOpacity: 1, weight: 2 }}>
                <LeafletTooltip>{t("logbookAudit.map.stop")}</LeafletTooltip>
              </CircleMarker>
            </Box>
          );
        })}
        <FitBounds bounds={bounds} />
      </MapContainer>
      <MapLegend />
      {withoutTrack > 0 && (
        <Box
          role="status"
          sx={{
            position: "absolute",
            left: 12,
            bottom: 28,
            zIndex: 1000,
            maxWidth: 420,
            p: "8px 12px",
            fontSize: 12.5,
            lineHeight: 1.45,
            color: AUDIT_TOKENS.text,
            bgcolor: AUDIT_TOKENS.surface,
            border: `1px solid ${AUDIT_TOKENS.line}`,
            borderRadius: `${AUDIT_TOKENS.radius.small}px`,
            boxShadow: AUDIT_TOKENS.shadow.panel,
          }}
        >
          {t("logbookAudit.map.noTrack", { count: withoutTrack })}
        </Box>
      )}
    </Box>
  );
}
